import type { ChannelClient } from '@hibro/platform-core';
import type {
  ILauncherService,
  IModuleService,
  IOverlayService,
  IPartitionService,
  IPermissionService,
  IProductConfigService,
  ISearchRecentService,
  IThemeService,
  IUpdateService,
  IWhenContextService,
  LaunchItem,
  LayoutArea,
  OverlayApi,
  PartitionSnapshot,
  ResolvedActivityBarItem,
  ResolvedContributions,
  SearchHitAnchor,
  SearchRecentEntry,
  SearchRecentSnapshot,
  ViewCachePolicy,
  WhenContext,
  WhenContextChange,
  WorkbenchNavigationChangedPayload,
} from '@hibro/shared';
import {
  BUILTIN_LAUNCH_ENTRIES,
  builtinLaunchKey,
  CONTRIBUTIONS_CHANGED_EVENT,
  DEFAULT_LAYOUT,
  EMPTY_EDITOR_PARTITION,
  evaluateWhen,
  isOpenViewRequest,
  LAUNCHER_CHANGED_EVENT,
  launchItemKey,
  MENU_LOCATION,
  NETWORK_STATUS_EVENT,
  PARTITION_CHANGED_EVENT,
  PLATFORM_MODULE_ID as SHARED_PLATFORM_MODULE_ID,
  readSettingsPageId,
  WHEN_CONTEXT_CHANGED_EVENT,
  WORKBENCH_EDITOR_SCOPE_RESET_EVENT,
} from '@hibro/shared';
import { Toaster, UiClientConfigProvider } from '@hibro/ui-client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { OpenExternalBridge } from '../app-links/open';
import {
  BUILTIN_COMMAND,
  BUILTIN_COMMANDS,
  withBuiltinCommands,
} from '../commands/builtin-commands';
import { createCommandMru } from '../commands/command-query';
import type { CommandRequestSource } from '../commands/command-requests';
import { resolveMenuLocation } from '../commands/menu-entries';
import { runOpenLinkCommand } from '../commands/open-link-command';
import type { WindowControlsBridge } from '../components/custom-header/custom-header';
import {
  ViewEventBusContext,
  type ViewEventSubscribe,
} from '../event-bus/event-bus';
import { useTranslator } from '../i18n/i18n';
import { formatChord } from '../keybindings/chord';
import {
  useKeybindingDispatch,
  useKeybindings,
} from '../keybindings/keybinding-host';
import {
  createLaunchActivator,
  type LaunchActivator,
} from '../launcher/launch-activation';
import { NativeContentsSuppressionProvider } from '../native-contents';
import {
  NavigationContext,
  type NavigationSource,
} from '../navigation/navigation';
import type { ViewTitleSource } from '../navigation/view-title';
import {
  CrashToast,
  type LogBridge,
  LogConsoleMirror,
} from '../panels/logs-view';
import { NotificationPrompt } from '../panels/notification-prompt';
import {
  type InAppNotificationBridge,
  NotificationToast,
} from '../panels/notification-toast';
import { PermissionPrompt } from '../panels/permission-prompt';
import {
  SETTINGS_WINDOW_FRAGMENT_ID,
  SETTINGS_WINDOW_HEIGHT,
  SETTINGS_WINDOW_VIEW_ID,
  SETTINGS_WINDOW_WIDTH,
} from '../panels/settings-window/ids';
import { settingsWindowLogError } from '../panels/settings-window/log';
import { UpdatePrompt } from '../panels/update-view';
import { type LoadUi, UiRegistry } from '../runtime/ui-registry';
import {
  createAppCommandProvider,
  createCommandProvider,
} from '../search/providers/command-provider';
import { createOpenCommandProvider } from '../search/providers/launch-providers';
import {
  appTabModuleIds,
  createModuleSearchProviders,
} from '../search/providers/module-provider';
import {
  createRecentLookup,
  createRecentProvider,
} from '../search/providers/recent-provider';
import {
  readSearchTabModuleId,
  type SearchResult,
  type SearchSeed,
} from '../search/search-contract';
import {
  COMMAND_PREFIX,
  commandArgsWithAnchor,
} from '../search/search-contract';
import type { DetachBridge } from '../surfaces/detach';
import { isDetachedInstance } from '../surfaces/detached-views';
import type { ExternalBridge } from '../surfaces/external-surface';
import type { IframeBridge } from '../surfaces/iframe-surface';
import { useOverlay } from '../surfaces/overlay-context';
import { OverlayProvider } from '../surfaces/overlay-host';
import { notifyViewOpened } from '../surfaces/staged-module-open';
import { SurfaceHostedInProvider } from '../surfaces/surface-hosted-in';
import { useUserActivation } from '../surfaces/user-activation-context';
import { resolveOverlayPortalContainer } from '../theme/portal-container';
import { ActivitySlot, type RailItems } from './activity';
import { DockviewTabHost } from './dockview';
import {
  diffClosedEditorInstances,
  type EditorInstanceCloseReason,
  editorInstanceCloseReason,
  EditorSlot,
  makeInstanceId,
  parseInstanceId,
  resolveViewCachePolicy,
} from './editor';
import {
  captureEditorPartition,
  diffEditorPartitionTabs,
  railViewAfterPartitionApply,
  shouldSaveEditorPartition,
} from './editor/editor-partition';
import { HeaderSlot, type SearchSuggestion } from './header';
import { filterLaunchItemsByWhen } from './launch-item-when';
import { WorkbenchLayout } from './layout';
import { resolveOpenViewRoute } from './open-view-route';
import { resolveStartupEditorView } from './startup-editor-view';

/**
 * 把本窗的 OverlayApi 交给工作台命令。`OverlayProvider` 是 {@link WorkbenchContent}
 * 的子节点，命令 handler 在父组件里，读不到 context。
 */
function CaptureOverlayApi({
  target,
}: {
  target: { current: OverlayApi | null };
}): null {
  target.current = useOverlay();
  return null;
}

/** `workbench.open` 的参数里那个可启动项身份（形状见 `launch-providers.OpenCommandArgs`）。 */
function readOpenTargetKey(arg: unknown): string {
  if (!arg || typeof arg !== 'object') {
    return '';
  }
  const key = (arg as { key?: unknown }).key;
  return typeof key === 'string' ? key : '';
}

function builtinLaunchItems(): LaunchItem[] {
  return BUILTIN_LAUNCH_ENTRIES.map((entry, index) => ({
    key: builtinLaunchKey(entry.id),
    source: 'builtin' as const,
    moduleId: SHARED_PLATFORM_MODULE_ID,
    title: entry.title,
    icon: entry.icon,
    action: entry.action,
    segment: entry.segment,
    // Fail-safe only: keep settings reachable even when layout/RPC is broken.
    pinned: true,
    locked: true,
    order: 10_000 + index,
  }));
}

/**
 * Client-side projection of manifest activityBar items (见 21).
 *
 * Used while the launcher RPC is loading, and as the first fallback when it
 * fails — so a stale SharedWorker / missing `launcher` channel does not blank
 * every module entry and leave only the built-in settings gear.
 */
function launchItemsFromContributions(
  activityBar: readonly ResolvedActivityBarItem[],
): LaunchItem[] {
  return activityBar.map((item, index) => ({
    key: launchItemKey(item.moduleId, item.id),
    source: 'module' as const,
    moduleId: item.moduleId,
    title: item.title,
    ...(item.icon ? { icon: item.icon } : {}),
    ...(item.iconActive ? { iconActive: item.iconActive } : {}),
    ...(item.action ? { action: item.action } : {}),
    ...(item.custom && item.viewId ? { customViewId: item.viewId } : {}),
    ...(item.showExpandedTitle === false ? { showExpandedTitle: false } : {}),
    segment: item.position ?? 'middle',
    pinned: true,
    locked: false,
    order: index,
  }));
}

function groupPinnedRail(items: readonly LaunchItem[]): RailItems {
  const pinned = items.filter((item) => item.pinned);
  const byOrder = (a: LaunchItem, b: LaunchItem): number => a.order - b.order;
  return {
    top: pinned.filter((item) => item.segment === 'top').sort(byOrder),
    middle: pinned.filter((item) => item.segment === 'middle').sort(byOrder),
    bottom: pinned.filter((item) => item.segment === 'bottom').sort(byOrder),
  };
}

/** Manifest order + built-ins (plan 见 21: first fallback before builtins-only). */
function contributionFallbackRail(
  activityBar: readonly ResolvedActivityBarItem[],
): RailItems {
  return groupPinnedRail([
    ...launchItemsFromContributions(activityBar),
    ...builtinLaunchItems(),
  ]);
}

/**
 * The two built-in ways into the header's global search field, both handled
 * locally (架构方案 §5.9).
 *
 * They differ only in what the field starts with: `commandPalette` seeds the
 * `/` prefix and so lands in command mode, `search` seeds nothing and shows the
 * empty state (recent searches, apps). `search` may also name an app tab
 * (`{ moduleId }`); without that argument the strip stays where it is.
 * One entry point could not do both modes — whichever prefix it picked made
 * the other unreachable from the keyboard.
 */
const SEARCH_COMMAND = 'workbench.commandPalette';
const SEARCH_PLAIN_COMMAND = 'workbench.search';
/** Built-in command/keybinding that opens the preferences window (§5.12). */
const SETTINGS_COMMAND = 'workbench.openSettings';
/** Product/deployment policy for the editor view opened on first workbench mount. */
const STARTUP_EDITOR_VIEW_CONFIG = 'workbench.startupEditorView';

const EMPTY_CONTRIBUTIONS: ResolvedContributions = {
  commands: [],
  views: [],
  shells: [],
  activityBar: [],
  menus: [],
  submenus: [],
  keybindings: [],
  search: [],
  searchFilters: [],
  hiddenAreas: [],
  setting: [],
  runtimeUiEntries: [],
};

/** Stable empty default for a module with no `hiddenAreas` declaration. */
const NO_HIDDEN_AREAS: readonly LayoutArea[] = [];

function editorInstanceKeys(
  tabs: readonly { viewId: string; instanceKey?: string }[],
): Set<string> {
  return new Set(
    tabs
      .filter((tab) => tab.instanceKey)
      .map((tab) => makeInstanceId(tab.viewId, tab.instanceKey)),
  );
}

/**
 * Top-level workbench orchestrator (架构方案 §5.7–§5.9).
 *
 * Fetches contributed commands/views/menus/keybindings/hiddenAreas, builds the
 * per-module UI registry, manages editor open/active tab state, wires shortcuts
 * and overlays (command palette, permissions, toasts), and composes
 * area-specific chrome (`HeaderSlot`, `ActivitySlot`, `EditorSlot`) as `slots`
 * for {@link WorkbenchLayout}. The tabbed `auxiliaryBar` gets an empty-fragment
 * slot (so no inline placeholder shows) and is empty-hidden by the visibility
 * coupling; it shows only its session tabs via `openAuxTab`.
 */
export interface WorkbenchProps {
  /** Auto RPC proxy for the host module service (架构方案 §5.1). */
  moduleService: IModuleService;
  /** Read-only product/deployment configuration resolved at boot (见 05). */
  productConfigService: IProductConfigService;
  /** Auto RPC proxy for the host permission broker (架构方案 §5.6). */
  permissionService: IPermissionService;
  /** Auto RPC proxy for the host overlay broker (架构方案 §5.8 OverlaySurface). */
  overlayService?: IOverlayService;
  /** Auto RPC proxy for the host theme service (见 13 / §5.10). */
  themeService: IThemeService;
  /** Underlying channel client, used here to subscribe to broadcast events. */
  client: ChannelClient;
  /** Lazy UI importers keyed by module id, discovered in the app shell. */
  uiLoaders: Map<string, LoadUi>;
  onOpenWindow?: () => void;
  /** Pop a detachable view into its own window (§5.8); injected by the shell. */
  onDetachView?: (
    viewId: string,
    moduleId: string,
    instanceKey?: string,
  ) => void;
  /**
   * Surfaces currently migrated into their own windows (§5.8), keyed by
   * serialized `DetachHandle` (see `detachedSurfaceKey`) rather than bare view
   * id — an editor view can have several instance tabs open and only the
   * detached one gives up its inline copy. The shell keeps this in sync across
   * windows; a migrated surface is removed from the inline layout (true
   * migration) and offered Focus/Attach controls instead.
   */
  detachedSurfaces?: ReadonlySet<string>;
  /** Attach a detached view back into this window (§5.8); injected by the shell. */
  onAttachView?: (viewId: string, instanceKey?: string) => void;
  /** Focus the window hosting a detached view (§5.8); injected by the shell. */
  onFocusDetached?: (viewId: string, instanceKey?: string) => void;
  /** Bridge for in-DOM iframe surfaces (见 9); injected by the shell. */
  iframeBridge?: IframeBridge;
  /** Bridge for external-process surfaces (§3.1/§5.8); injected by the shell. */
  externalBridge?: ExternalBridge;
  /**
   * Source of `navigate(view:<id>, …)` locations for inline-dom views (见 8);
   * injected by the shell. Provided to descendants via `NavigationContext`.
   */
  navigation?: NavigationSource;
  /**
   * Iframe `setTitle` calls for this window (见 3 / 11). Injected by the shell.
   * Each call writes one dockview panel; a panel this window does not hold is
   * left alone.
   */
  viewTitles?: ViewTitleSource;
  /**
   * Commands requested by native chrome outside any window — the tray menu
   * (见 23); injected by the shell. Dispatched through the same `moduleService`
   * a palette entry uses, so built-ins are handled locally and the rest reach
   * the extension host.
   */
  commandRequests?: CommandRequestSource;
  /**
   * In-app notifications routed here by the host (见 23); injected by the shell.
   * Absent on a runtime with no delivery path, which is how "no toast ever
   * arrives" stays the absence of a bridge rather than a branch inside the sink.
   */
  inAppNotifications?: InAppNotificationBridge;
  /** Process keys of running external processes (§3.1/§5.8: `<moduleId>` or
   * `<moduleId>#<entryId>`); drives the activityBar run indicator. */
  runningExternalProcesses?: ReadonlySet<string>;
  /** Module ids whose lazy `dist/` is downloading/unpacking (见 12 两阶段下载);
   * the matching activityBar icon pulses in place. */
  distFetchingModules?: ReadonlySet<string>;
  /** Focus a running external process's native window by process key (§5.8). */
  onActivateExternal?: (processKey: string) => void;
  /**
   * Log sink bridge (见 15 / §5.16); crash toasts and command failures.
   * Injected by the shell from the preload `window.hibroLog` global.
   */
  logBridge?: LogBridge;
  /** Launcher projection + rail layout (见 21). */
  launcherService: ILauncherService;
  /** Recent global-search picks (需求 FR-14 §4.2.1); drives the recent section. */
  searchRecentService: ISearchRecentService;
  /**
   * Application update state (见 25); drives the shared prompt and the settings
   * About section. Not optional like the bridges above: every runtime serves the
   * channel, and a runtime that cannot update says so through
   * `UpdateCapabilities` rather than by leaving the service out.
   */
  updateService: IUpdateService;
  /**
   * Module-published `when` context keys (见 03 §5.9). Held by the host so every
   * window evaluates the same values; read at boot and on reconnect.
   */
  whenContextService: IWhenContextService;
  /**
   * Workbench partition key and the editor-tab buckets it names (见 03).
   * Read at boot: a segment may have been published before this window existed.
   */
  partitionService: IPartitionService;
  /**
   * Sync open for the browser runtime (见 21 / 18). Must run inside the click
   * handler; routing through the extension host drops the user gesture.
   */
  openExternalBridge?: OpenExternalBridge;
  /**
   * Mirror the whole log pipeline onto this window's DevTools console (见 15).
   * Driven by the Developer-section toggle; off unless explicitly enabled, so a
   * packaged build stays quiet until a teammate turns it on.
   */
  mirrorConsole?: boolean;
  /**
   * Custom title-bar window controls bridge (§5.8); injected by the shell. When
   * provided, the header renders minimize/maximize/close on Windows/Linux (and
   * defers to native traffic lights on macOS).
   */
  windowControlsBridge?: WindowControlsBridge;
  /**
   * Detach mechanism bridge (§5.8 通用 Detachable); injected by the shell and
   * turned into `ViewProps.detach`/`detached` for source views (见 16).
   */
  detachBridge?: DetachBridge;
  /**
   * Fired when a dockview editor instance tab leaves the strip so runtimes can
   * drop per-tab resources (built-in browser slots, 见 22). `partition` means
   * the tab was parked for another key and must keep anything needed to reopen
   * it; `discard` means the user closed it.
   */
  onCloseViewInstance?: (
    viewId: string,
    instanceKey: string,
    reason: EditorInstanceCloseReason,
  ) => void;
}

/**
 * Workbench shell (架构方案 §5.7). Fetches all contribution points via the RPC
 * proxy and drives a schema-based layout: contributed views render through the
 * inline-dom SurfaceHost (§5.8), menus and a command palette execute commands
 * (§5.9), and keybindings map keystrokes to commands. It also keeps the
 * cross-window event counter from the communication-substrate slice.
 */
/** Public Workbench entry point (架构方案 §5.7). */
export function Workbench(props: WorkbenchProps) {
  // Bind the general EventBus subscription (架构方案 §5.15) to the live channel so
  // inline-dom views get `ViewProps.onEvent`. Memoized per client so a reconnect
  // (fresh client) re-binds while ordinary re-renders keep the same identity —
  // matching the stable-callback contract in `surface-host`.
  const { client } = props;
  const onEvent = useMemo<ViewEventSubscribe>(
    () => (event, handler) => client.listen(event, handler),
    [client],
  );
  return (
    <SurfaceHostedInProvider value="workbench">
      <ViewEventBusContext.Provider value={onEvent}>
        <NavigationContext.Provider value={props.navigation}>
          <WorkbenchContent {...props} />
        </NavigationContext.Provider>
      </ViewEventBusContext.Provider>
    </SurfaceHostedInProvider>
  );
}

function WorkbenchContent({
  moduleService: hostModuleService,
  productConfigService,
  permissionService,
  overlayService,
  themeService,
  client,
  uiLoaders,
  onOpenWindow,
  onDetachView,
  detachedSurfaces,
  onAttachView,
  onFocusDetached,
  iframeBridge,
  externalBridge,
  navigation,
  viewTitles,
  commandRequests,
  inAppNotifications,
  runningExternalProcesses,
  distFetchingModules,
  onActivateExternal,
  logBridge,
  launcherService,
  searchRecentService,
  updateService,
  whenContextService,
  partitionService,
  openExternalBridge,
  mirrorConsole,
  windowControlsBridge,
  detachBridge,
  onCloseViewInstance,
}: WorkbenchProps) {
  const [contributions, setContributions] =
    useState<ResolvedContributions>(EMPTY_CONTRIBUTIONS);
  const [contributionsLoaded, setContributionsLoaded] = useState(false);
  /** `undefined` = still loading; `null` = failed (fail-safe builtins only). */
  const [launchItems, setLaunchItems] = useState<
    LaunchItem[] | null | undefined
  >(undefined);
  const [error, setError] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  /** Read inside async callbacks (见 `recordSearchPick`), where state is stale. */
  const searchOpenRef = useRef(false);
  /**
   * Text to seed the search field with, and a token that makes each invocation
   * distinct.
   *
   * The token is what lets the *same* chord act while the box is already open:
   * pressing the palette chord over a half-typed search has to switch it into
   * command mode, and a bare string prop would compare equal and do nothing.
   */
  const [searchSeed, setSearchSeed] = useState<SearchSeed | null>(null);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [permissionPromptOpen, setPermissionPromptOpen] = useState(false);
  const [badge, setBadge] = useState(0);
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  const nativeContentsSuppressed =
    overlayOpen || searchOpen || permissionPromptOpen;
  /** Module-published `when` keys (见 03 §5.9); empty until the host answers. */
  const [moduleContextKeys, setModuleContextKeys] = useState<WhenContext>({});
  /** Bumped on workspace switch to remount {@link EditorSlot} and drop MRU cache. */
  const [editorScopeEpoch, setEditorScopeEpoch] = useState(0);
  // Editor-area single-view host (架构方案 §5.7): the current active view
  // instance id (`viewId` or `viewId::instanceKey`). Only this view mounts a
  // SurfaceView, so opening drives module activate/deactivate on demand (§5.4).
  // Multi-tab is deferred to native dockview tabs.
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const activeViewIdRef = useRef<string | null>(null);
  activeViewIdRef.current = activeViewId;
  const partitionKeyRef = useRef('');
  const editorClockRef = useRef(0);
  /** Set by editor-scope reset so the next partition event does not save the cleared strip. */
  const suppressPartitionSaveRef = useRef(false);
  /** True while a partition snapshot is being applied, so tab events do not record the half-built strip. */
  const applyingPartitionRef = useRef(false);
  /**
   * Set before the editor tab host drops its layout. `getTabs()` is then empty
   * because the layout is gone, not because the user closed every document.
   */
  const editorLayoutGoneRef = useRef(false);
  const partitionReadyRef = useRef(false);
  const partitionApplyRef = useRef(0);
  const savePartitionTabsRef = useRef<() => void>(() => undefined);
  const startupResolvedRef = useRef(false);
  const startupRequestIdRef = useRef(0);
  const contributedViewsRef = useRef(contributions.views);
  contributedViewsRef.current = contributions.views;
  const moduleIdForView = useCallback(
    (viewId: string) =>
      contributedViewsRef.current.find((view) => view.id === viewId)?.moduleId,
    [],
  );
  // A tab's keep-alive policy comes from the same `resolveViewCachePolicy` the
  // EditorSlot MRU cache uses (§5.7 视图缓存), so both presentations of a view
  // agree on whether switching away may destroy its DOM.
  const cachePolicyForView = useCallback((viewId: string): ViewCachePolicy => {
    const view = contributedViewsRef.current.find((v) => v.id === viewId);
    return view ? resolveViewCachePolicy(view) : 'none';
  }, []);
  const areaForView = useCallback(
    (viewId: string) =>
      contributedViewsRef.current.find((view) => view.id === viewId)?.area,
    [],
  );
  const auxiliaryTabHost = useMemo(
    () =>
      new DockviewTabHost(
        'auxiliaryBar',
        moduleIdForView,
        cachePolicyForView,
        areaForView,
      ),
    [areaForView, moduleIdForView, cachePolicyForView],
  );
  // Editor-area tab strip (§5.7 / 见 20 二期): document-like instances open here
  // as native tabs. Only *instance-keyed* opens route to it — a rail click carries
  // no key and keeps landing in the single-active EditorSlot below.
  const editorTabHost = useMemo(
    () => new DockviewTabHost('editor', moduleIdForView, cachePolicyForView),
    [moduleIdForView, cachePolicyForView],
  );
  const { title, t, locale } = useTranslator();
  const translateRef = useRef(t);
  translateRef.current = t;
  const titleRef = useRef(title);
  titleRef.current = title;
  // Closing a tab must update the current key's strip. Ctrl+R reloads this
  // window but not the host, so a snapshot taken only on key change would
  // reopen the tab that was just closed.
  savePartitionTabsRef.current = () => {
    if (
      editorLayoutGoneRef.current ||
      !shouldSaveEditorPartition({
        key: partitionKeyRef.current,
        suppress: suppressPartitionSaveRef.current,
        applying: applyingPartitionRef.current,
        ready: partitionReadyRef.current,
      })
    ) {
      return;
    }
    void partitionService
      .setEditorTabs(
        partitionKeyRef.current,
        captureEditorPartition(editorTabHost.getTabs()),
        editorClockRef.current,
      )
      .catch((error: unknown) => {
        logBridge?.append({
          level: 'error',
          kind: 'log',
          scope: 'partition',
          message: 'Failed to store the editor tabs for the current partition',
          error: {
            message: error instanceof Error ? error.message : String(error),
          },
        });
      });
  };
  useEffect(() => {
    return editorTabHost.subscribe(() => {
      savePartitionTabsRef.current();
    });
  }, [editorTabHost]);
  useEffect(() => {
    const flush = (): void => {
      savePartitionTabsRef.current();
    };
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
    };
  }, []);
  // `OverlayProvider` 在这棵树的子节点上，handler 自己读不到 context。子组件把
  // 本窗的 OverlayApi 写进这个 ref，命令执行时再读——读到空就记错误，不静默返回。
  const overlayApiRef = useRef<OverlayApi | null>(null);
  useEffect(() => () => auxiliaryTabHost.detach(), [auxiliaryTabHost]);
  useEffect(
    () => () => {
      editorLayoutGoneRef.current = true;
      editorTabHost.detach();
    },
    [editorTabHost],
  );
  // Through a ref, like `contributedViewsRef` above: hosts pass this as an
  // inline arrow, and re-subscribing on every workbench render would rebuild
  // the baseline the close diff is computed against.
  const closeViewInstanceRef = useRef(onCloseViewInstance);
  closeViewInstanceRef.current = onCloseViewInstance;
  useEffect(() => {
    // `getTabs()` is only the anchor group. A tab dragged into another group
    // leaves that list while `isOpen` (whole-layout `getPanel`) stays true;
    // disposing it there kills a page the user is still looking at (见 22).
    let tracked = editorInstanceKeys(editorTabHost.getTabs());
    return editorTabHost.subscribe(() => {
      const diff = diffClosedEditorInstances(
        tracked,
        editorInstanceKeys(editorTabHost.getTabs()),
        (id) => {
          const parsed = parseInstanceId(id);
          return editorTabHost.isOpen(parsed.viewId, parsed.instanceKey);
        },
      );
      tracked = diff.tracked;
      for (const id of diff.closed) {
        const parsed = parseInstanceId(id);
        if (parsed.instanceKey) {
          closeViewInstanceRef.current?.(
            parsed.viewId,
            parsed.instanceKey,
            editorInstanceCloseReason(applyingPartitionRef.current),
          );
        }
      }
    });
  }, [editorTabHost]);

  /**
   * `workbench.open` 的执行面（命中行由 `launch-providers` 合成，见那里的 `/open <module>`）。
   *
   * 目标只以身份进来（`{ key }`）——命令参数带得动的就这些；打开漏斗要的是整个 `LaunchItem`
   * （detached / `resolveInstance` / 外部进程三条分支不是数据能表达的，见 `21`），所以这里回查。
   *
   * 走 ref 而不是把可启动项与 activator 塞进依赖，两个理由：`moduleService` 会递给下面每一个
   * 消费方，让它随可启动项变化换引用不值当；而 activator 自己又依赖 `moduleService`，直接引
   * 用就成环了。同款写法见上面的 `closeViewInstanceRef`。
   */
  const openTargetsRef = useRef<{
    items: readonly LaunchItem[];
    activate: LaunchActivator | null;
  }>({ items: [], activate: null });
  const runOpenCommand = useCallback(
    (...args: unknown[]) => {
      const key = readOpenTargetKey(args[0]);
      const { items, activate } = openTargetsRef.current;
      const item = items.find((candidate) => candidate.key === key);
      if (!key || !item || !activate) {
        // 目标解析不出来就说出来，别静默不动：调用方多半传错了 key，或那个模块刚被卸掉。
        logBridge?.append({
          level: 'warn',
          kind: 'log',
          scope: 'search',
          message: `${BUILTIN_COMMAND.open}: no launchable for the requested target`,
          data: { key: key || null },
        });
        return;
      }
      activate(item, 'search');
    },
    [logBridge],
  );

  const runOpenLink = useCallback(
    (...args: unknown[]) => {
      const overlay = overlayApiRef.current;
      const openLink = hostModuleService.openLink?.bind(hostModuleService);
      if (!overlay) {
        logBridge?.append({
          level: 'error',
          kind: 'log',
          scope: 'commands',
          message: 'workbench.openLink has no overlay prompt',
        });
        return;
      }
      if (!openLink) {
        logBridge?.append({
          level: 'error',
          kind: 'log',
          scope: 'commands',
          message: 'workbench.openLink has no openLink on the module service',
        });
        return;
      }
      const translate = translateRef.current;
      void runOpenLinkCommand(args, {
        prompt: (options) => overlay.prompt(options),
        promptOptions: () => ({
          title: translate('platform:command.openLink.prompt.title'),
          message: translate('platform:command.openLink.prompt.message'),
          placeholder: translate(
            'platform:command.openLink.prompt.placeholder',
          ),
          confirmText: translate('platform:command.openLink.prompt.confirm'),
          cancelText: translate('platform:command.openLink.prompt.cancel'),
        }),
        openLink: (url, callerModuleId) => openLink(url, callerModuleId),
        log: {
          error: (message, data) => {
            logBridge?.append({
              level: 'error',
              kind: 'log',
              scope: 'commands',
              message,
              ...(data === undefined ? {} : { data }),
            });
          },
        },
      });
    },
    [hostModuleService, logBridge],
  );

  /**
   * Open the search box with `text` already in the field (`/` = command mode).
   *
   * Both search commands and the header's own affordances funnel through here,
   * so "what does opening the box mean" has one definition.
   */
  const openSearch = useCallback((text: string, tabModuleId?: string) => {
    setSearchOpen(true);
    setSearchSeed((previous) => ({
      text,
      token: (previous?.token ?? 0) + 1,
      ...(tabModuleId ? { tabModuleId } : {}),
    }));
  }, []);

  const openPreferencesWindow = useCallback(
    (requested?: string) => {
      const pageId = readSettingsPageId(requested);
      if (pageId) {
        void hostModuleService
          .selectSettingsPage?.(pageId)
          .catch((err: unknown) => {
            settingsWindowLogError('settings window page select failed', {
              reason: err instanceof Error ? err.message : String(err),
            });
          });
      }
      if (!detachBridge) {
        settingsWindowLogError(
          'settings window open failed: detach bridge missing',
        );
        return;
      }
      void detachBridge
        .detachFragment({
          viewId: SETTINGS_WINDOW_VIEW_ID,
          moduleId: SHARED_PLATFORM_MODULE_ID,
          fragmentId: SETTINGS_WINDOW_FRAGMENT_ID,
          mode: 'mirror',
          chrome: false,
          width: SETTINGS_WINDOW_WIDTH,
          height: SETTINGS_WINDOW_HEIGHT,
          minWidth: SETTINGS_WINDOW_WIDTH,
          minHeight: SETTINGS_WINDOW_HEIGHT,
          resizable: true,
          ...(pageId ? { payload: { pageId } } : {}),
        })
        .catch((err: unknown) => {
          settingsWindowLogError('settings window open failed', {
            reason: err instanceof Error ? err.message : String(err),
          });
        });
    },
    [detachBridge, hostModuleService],
  );

  // Built-in commands are served here and everything else forwarded to the host
  // (架构方案 §5.9). Decorating the service once means every consumer below —
  // the dispatcher, the rail, tab controllers, each view's
  // `ViewProps.executeCommand` — can invoke them, since they all receive this
  // same instance.
  const moduleService = useMemo(
    () =>
      withBuiltinCommands(hostModuleService, {
        // 需求 §4.1: the shortcut and the header field are the same component.
        // Which mode it lands in is the command's whole difference (见上
        // `SEARCH_COMMAND`).
        [BUILTIN_COMMAND.commandPalette]: () => openSearch(COMMAND_PREFIX),
        [BUILTIN_COMMAND.search]: (...args) =>
          openSearch('', readSearchTabModuleId(args[0])),
        [BUILTIN_COMMAND.openSettings]: (...args) =>
          openPreferencesWindow(readSettingsPageId(args[0])),
        [BUILTIN_COMMAND.openSettingsWindow]: (...args) =>
          openPreferencesWindow(readSettingsPageId(args[0])),
        [BUILTIN_COMMAND.openLink]: (...args) => runOpenLink(...args),
        [BUILTIN_COMMAND.open]: (...args) => runOpenCommand(...args),
        [BUILTIN_COMMAND.toggleFullScreen]: () => {
          if (!windowControlsBridge) {
            logBridge?.append({
              level: 'error',
              kind: 'log',
              scope: 'commands',
              message:
                'workbench.toggleFullScreen has no window controls bridge',
            });
            return;
          }
          windowControlsBridge.toggleFullScreen();
        },
      }),
    // `runOpenCommand` 读 ref、身份稳定，故不进依赖：`moduleService` 被递给下面所有消费方，
    // 让它随可启动项变化而换引用是不值当的。
    [
      hostModuleService,
      runOpenCommand,
      runOpenLink,
      openSearch,
      openPreferencesWindow,
      windowControlsBridge,
      logBridge,
    ],
  );
  // One history for the session; the palette reads it to rank and writes on run.
  const commandMru = useMemo(() => createCommandMru(), []);
  const allCommands = useMemo(
    () => [...BUILTIN_COMMANDS, ...contributions.commands],
    [contributions.commands],
  );

  const refresh = useCallback(() => {
    moduleService
      .listContributions()
      .then((next) => {
        setContributions(next);
        setContributionsLoaded(true);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
        setContributionsLoaded(true);
      });
  }, [moduleService]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Re-pull contributions when the active module set changes at runtime (doc
  // 37) — e.g. a bundle was installed and hot-registered. A platform public
  // event, so no declaration is needed.
  useEffect(() => {
    const dispose = client.listen(CONTRIBUTIONS_CHANGED_EVENT, () => {
      refresh();
    });
    return dispose;
  }, [client, refresh]);

  const refreshLaunchItems = useCallback(() => {
    launcherService
      .listItems()
      .then((next) => setLaunchItems(next))
      .catch(() => {
        // First fallback is contribution-based (see `railItems` below), not
        // builtins-only — a missing/stale `launcher` channel on web (SharedWorker
        // still serving an older host) used to blank every module entry.
        setLaunchItems(null);
      });
  }, [launcherService]);

  useEffect(() => {
    refreshLaunchItems();
  }, [refreshLaunchItems]);

  useEffect(() => {
    const dispose = client.listen(LAUNCHER_CHANGED_EVENT, () => {
      refreshLaunchItems();
    });
    return dispose;
  }, [client, refreshLaunchItems]);

  // One retry after contributions settle: the first listItems call can race a
  // SharedWorker that has not yet registered the launcher channel.
  const launcherRetriedRef = useRef(false);
  useEffect(() => {
    if (
      launchItems === null &&
      contributionsLoaded &&
      !launcherRetriedRef.current
    ) {
      launcherRetriedRef.current = true;
      refreshLaunchItems();
    }
  }, [contributionsLoaded, launchItems, refreshLaunchItems]);

  // Let an owner view observe contribution-point changes (见 17): the same
  // public `CONTRIBUTIONS_CHANGED_EVENT` fired on module (un)register, passed to
  // `SurfaceView` so `ViewProps.contributions.onDidChangeContributions` works.
  const subscribeContributionsChanged = useCallback(
    (listener: () => void) =>
      client.listen(CONTRIBUTIONS_CHANGED_EVENT, listener),
    [client],
  );

  useEffect(() => {
    const dispose = client.listen(NETWORK_STATUS_EVENT, (data) => {
      setOnline((data as { online: boolean }).online);
    });
    return dispose;
  }, [client]);

  // Module-published `when` keys (见 03 §5.9). Read now and re-read on every
  // reconnect, because an `onStartup` module publishes before this renderer
  // connects; the broadcast alone would miss those. Mirrors the theme/gate
  // read-then-subscribe idiom in `boot.tsx`.
  useEffect(() => {
    let active = true;
    const applySnapshot = (next: WhenContext): void => {
      if (active) {
        setModuleContextKeys(next);
      }
    };
    whenContextService
      .getSnapshot()
      .then(applySnapshot)
      .catch((error: unknown) => {
        // Keep the empty snapshot: a `when` clause naming a key nobody published
        // stays false, so items gate themselves off rather than showing against
        // state we could not read.
        logBridge?.append({
          level: 'warn',
          kind: 'log',
          scope: 'when-context',
          message: 'Failed to read the when-context snapshot; keys stay unset',
          error: {
            message: error instanceof Error ? error.message : String(error),
          },
        });
      });
    const dispose = client.listen(WHEN_CONTEXT_CHANGED_EVENT, (data) => {
      applySnapshot((data as WhenContextChange).context);
    });
    return () => {
      active = false;
      dispose();
    };
  }, [client, whenContextService, logBridge]);

  // `resetEditorScope` drops every editor-tab bucket and the keep-alive built
  // for the previous account. Do not write the strip we are about to clear
  // back under the old key.
  useEffect(() => {
    const dispose = client.listen(WORKBENCH_EDITOR_SCOPE_RESET_EVENT, () => {
      suppressPartitionSaveRef.current = true;
      for (const tab of editorTabHost.getTabs()) {
        if (tab.instanceKey) {
          editorTabHost.close(tab.viewId, tab.instanceKey);
        }
      }
      editorTabHost.showAnchor();
      setEditorScopeEpoch((epoch) => epoch + 1);
      startupResolvedRef.current = true;
      void productConfigService
        .getValue(STARTUP_EDITOR_VIEW_CONFIG)
        .then((configuredViewId) => {
          const target = resolveStartupEditorView(
            configuredViewId,
            contributedViewsRef.current,
          );
          setActiveViewId(target?.id ?? null);
        })
        .catch(() => {
          setActiveViewId(null);
        });
      void partitionService.getSnapshot().then((view) => {
        editorClockRef.current = view.editorClock;
        partitionKeyRef.current = view.key;
      });
    });
    return dispose;
  }, [client, editorTabHost, partitionService, productConfigService]);

  // Swap the editor strip when the partition key changes (见 03). The host
  // does not say what the key means. An empty key is not a bucket.
  useEffect(() => {
    if (!contributionsLoaded) {
      return;
    }
    let alive = true;
    const seedStartupRail = (generation: number): void => {
      void productConfigService
        .getValue(STARTUP_EDITOR_VIEW_CONFIG)
        .then((configuredViewId) => {
          if (
            !alive ||
            generation !== partitionApplyRef.current ||
            activeViewIdRef.current !== null
          ) {
            return;
          }
          const target = resolveStartupEditorView(
            configuredViewId,
            contributedViewsRef.current,
          );
          // Do not reveal the anchor. A restored document tab stays in front;
          // with no instance tab the anchor is already the group's active panel.
          setActiveViewId(target?.id ?? null);
        })
        .catch((error: unknown) => {
          logBridge?.append({
            level: 'warn',
            kind: 'log',
            scope: 'partition',
            message: 'Failed to open the startup editor',
            error: {
              message: error instanceof Error ? error.message : String(error),
            },
          });
        });
    };
    const apply = (nextKey: string): void => {
      const generation = ++partitionApplyRef.current;
      startupResolvedRef.current = true;
      const load =
        nextKey === ''
          ? Promise.resolve(EMPTY_EDITOR_PARTITION)
          : partitionService.getEditorTabs(nextKey);
      void load
        .then((snapshot) => {
          if (!alive || generation !== partitionApplyRef.current) {
            return;
          }
          applyingPartitionRef.current = true;
          try {
            const current = editorTabHost
              .getTabs()
              .flatMap((tab) =>
                tab.instanceKey
                  ? [{ viewId: tab.viewId, instanceKey: tab.instanceKey }]
                  : [],
              );
            const diff = diffEditorPartitionTabs(current, snapshot.tabs);
            for (const tab of diff.close) {
              editorTabHost.close(tab.viewId, tab.instanceKey);
            }
            for (const tab of diff.open) {
              const view = contributedViewsRef.current.find(
                (item) => item.id === tab.viewId,
              );
              if (!view || view.area !== 'editor') {
                continue;
              }
              editorTabHost.open({
                viewId: tab.viewId,
                moduleId: view.moduleId,
                instanceKey: tab.instanceKey,
                title: titleRef.current(view.moduleId, view.title ?? view.id),
                activate: false,
              });
            }
            if (snapshot.focusedInstanceId) {
              const parsed = parseInstanceId(snapshot.focusedInstanceId);
              if (parsed.instanceKey) {
                editorTabHost.focus(parsed.viewId, parsed.instanceKey);
              }
            } else {
              editorTabHost.showAnchor();
            }
            // Document tabs only. The saved rail singleton is not applied.
            if (
              railViewAfterPartitionApply(activeViewIdRef.current) === 'startup'
            ) {
              seedStartupRail(generation);
            }
            setEditorScopeEpoch((epoch) => epoch + 1);
          } finally {
            applyingPartitionRef.current = false;
          }
          partitionReadyRef.current = true;
          savePartitionTabsRef.current();
        })
        .catch((error: unknown) => {
          logBridge?.append({
            level: 'error',
            kind: 'log',
            scope: 'partition',
            message: 'Failed to load the editor tabs for the current partition',
            error: {
              message: error instanceof Error ? error.message : String(error),
            },
          });
        });
    };
    const onKey = (nextKey: string): void => {
      const previous = partitionKeyRef.current;
      const save = !suppressPartitionSaveRef.current && previous !== '';
      suppressPartitionSaveRef.current = false;
      if (save) {
        void partitionService
          .setEditorTabs(
            previous,
            captureEditorPartition(editorTabHost.getTabs()),
            editorClockRef.current,
          )
          .catch((error: unknown) => {
            logBridge?.append({
              level: 'error',
              kind: 'log',
              scope: 'partition',
              message:
                'Failed to store the editor tabs for the current partition',
              error: {
                message: error instanceof Error ? error.message : String(error),
              },
            });
          });
      }
      partitionKeyRef.current = nextKey;
      apply(nextKey);
    };
    void partitionService
      .getSnapshot()
      .then((view) => {
        if (!alive) {
          return;
        }
        editorClockRef.current = view.editorClock;
        onKey(view.key);
      })
      .catch((error: unknown) => {
        logBridge?.append({
          level: 'error',
          kind: 'log',
          scope: 'partition',
          message: 'Failed to read the workbench partition key',
          error: {
            message: error instanceof Error ? error.message : String(error),
          },
        });
      });
    const dispose = client.listen(PARTITION_CHANGED_EVENT, (data) => {
      onKey((data as PartitionSnapshot).key);
    });
    return () => {
      alive = false;
      dispose();
    };
  }, [
    client,
    editorTabHost,
    logBridge,
    partitionService,
    productConfigService,
    contributionsLoaded,
  ]);

  // Open an editor view (架构方案 §5.7): make it the single active view. The
  // `instanceKey` (§5.7 视图多实例) is carried on the id so per-instance surfaces
  // (e.g. the AI panel keyed by sessionId) mount correctly; a keyless call keeps
  // the plain `viewId`. Opening another view replaces the current one (no tabs).
  const openView = useCallback(
    (viewId: string, instanceKey?: string) => {
      notifyViewOpened(viewId);
      // A direct user/command/navigation choice always wins over an in-flight
      // startup-config read.
      startupResolvedRef.current = true;
      setActiveViewId(makeInstanceId(viewId, instanceKey));
      // EditorSlot lives on the structural anchor. Instance tabs share that
      // group; without revealing the anchor, a rail click only updates React
      // state behind the still-visible document tab.
      editorTabHost.showAnchor();
    },
    [editorTabHost],
  );

  const reportNavigation = useCallback(
    (payload: WorkbenchNavigationChangedPayload) => {
      void moduleService.reportNavigation?.(payload).catch(() => undefined);
    },
    [moduleService],
  );

  // Route an OpenViewRequest to its presentation (架构方案 §5.7): the decision
  // itself is `resolveOpenViewRoute`, so the invariant it encodes — a rail click
  // never opens a tab — is testable without a workbench. Both tab hosts queue
  // their intents until layout restore finishes.
  const routeOpenView = useCallback(
    (viewId: string, instanceKey?: string) => {
      const view = contributions.views.find((v) => v.id === viewId);
      const route = resolveOpenViewRoute(view?.area, instanceKey);
      if (route === 'activeEditorView' || !view) {
        openView(viewId, instanceKey);
        return;
      }
      notifyViewOpened(viewId);
      const host = route === 'auxiliaryTab' ? auxiliaryTabHost : editorTabHost;
      // The declared view title is the opening label. A source module renames
      // the tab from its chrome via `TabHostApi.setTitle`; an iframe page
      // calls `sdk.setTitle`, which lands on the same write for this
      // connection's instance.
      host.open({
        viewId,
        moduleId: view.moduleId,
        instanceKey,
        title: title(view.moduleId, view.title ?? view.id),
      });
    },
    [contributions.views, auxiliaryTabHost, editorTabHost, title, openView],
  );

  // Resolve the product-selected editor only after contribution discovery, so
  // an invalid/uninstalled id fails closed to the existing editor empty state.
  // The request generation also makes StrictMode effect replay and a live
  // contribution refresh harmless; a user action marks startup resolved first.
  useEffect(() => {
    if (!contributionsLoaded || startupResolvedRef.current) {
      return;
    }

    const requestId = ++startupRequestIdRef.current;
    void productConfigService
      .getValue(STARTUP_EDITOR_VIEW_CONFIG)
      .then((configuredViewId) => {
        if (
          requestId !== startupRequestIdRef.current ||
          startupResolvedRef.current
        ) {
          return;
        }
        startupResolvedRef.current = true;
        const target = resolveStartupEditorView(
          configuredViewId,
          contributions.views,
        );
        if (target) {
          // Seed the singleton view *without* revealing the anchor: a restored
          // document tab was in front when the user reloaded and has to stay
          // there. Routing through `openView` here would activate the anchor on
          // every boot, which both throws away what the user was looking at and
          // makes the restored tab load its content for nothing (§5.7 视图缓存
          // 的懒挂). With no instance tabs the anchor is the group's active
          // panel already, so the startup view still lands in front.
          setActiveViewId(target.id);
        }
      })
      .catch((err: unknown) => {
        if (requestId !== startupRequestIdRef.current) {
          return;
        }
        startupResolvedRef.current = true;
        setError(err instanceof Error ? err.message : String(err));
      });
  }, [contributions.views, contributionsLoaded, productConfigService]);

  const dispatch = useCallback(
    (command: string, ...args: unknown[]) => {
      if (command === SEARCH_COMMAND) {
        openSearch(COMMAND_PREFIX);
        return;
      }
      if (command === SEARCH_PLAIN_COMMAND) {
        openSearch('', readSearchTabModuleId(args[0]));
        return;
      }
      if (command === SETTINGS_COMMAND) {
        reportNavigation({ source: 'settings', moduleId: null });
        openPreferencesWindow(readSettingsPageId(args[0]));
        return;
      }
      moduleService
        .executeCommand(command, ...args)
        .then((result) => {
          setError(null);
          // A command wired to an activityBar `command` action may return an
          // OpenViewRequest to open/focus an editor view instance (§5.7 视图多
          // 实例), e.g. a launcher that opens a fresh session tab each click.
          if (isOpenViewRequest(result)) {
            routeOpenView(
              result.__appOpenView.viewId,
              result.__appOpenView.instanceKey,
            );
          }
        })
        .catch((err: unknown) => {
          setError(err instanceof Error ? err.message : String(err));
        });
    },
    [
      moduleService,
      routeOpenView,
      reportNavigation,
      openSearch,
      openPreferencesWindow,
    ],
  );

  // Build the UI registry from shell-provided loaders + fetched views; rebuilds
  // only when the view set changes (i.e. on refresh), preserving its activation
  // cache across ordinary re-renders.
  const registry = useMemo(
    () =>
      new UiRegistry(
        uiLoaders,
        contributions.views,
        undefined,
        contributions.runtimeUiEntries,
      ),
    [uiLoaders, contributions.views, contributions.runtimeUiEntries],
  );

  // Editor-area views are opened on demand into tabs (below). The activityBar
  // rail is driven by the merged LaunchItem projection (见 21): module
  // contributions, built-ins and app links after rail-layout merge.
  const editorViews = useMemo(
    () => contributions.views.filter((view) => view.area === 'editor'),
    [contributions.views],
  );
  const layoutViews = useMemo(
    () =>
      contributions.views.filter(
        (view) =>
          view.area !== 'editor' &&
          view.area !== 'activityBar' &&
          // Overlay-only views (架构方案 §5.8) are never docked into a layout area;
          // they are mounted on demand by the OverlayHost via openModal/openDrawer.
          view.area !== 'overlay' &&
          // Gate-only views (架构方案 §5.8 App Gate) full-screen replace the whole
          // workbench via GateHost; they never dock into a layout area.
          view.area !== 'gate' &&
          // Tabbed host area: auxiliaryBar views are native dockview tabs via
          // TabHost (see `allViews` for the full set), so keep them out of the
          // inline-docked `views` to avoid a double mount alongside their tab.
          view.area !== 'auxiliaryBar',
      ),
    [contributions.views],
  );

  // Clear the active view when its declaration no longer exists (架构方案
  // §5.4.1, 见 12): a module uninstall (or any contribution change) can drop the
  // editor view that is currently shown, so reconcile `activeViewId` (by its base
  // `viewId`) against the live editor-view set to avoid a dangling surface.
  useEffect(() => {
    const liveIds = new Set(editorViews.map((view) => view.id));
    setActiveViewId((prev) =>
      prev !== null && !liveIds.has(parseInstanceId(prev).viewId) ? null : prev,
    );
  }, [editorViews]);

  // Resolve the active tab's view *declaration* by its base `viewId` (§5.7);
  // instances of the same view share one declaration (title, kind, …).
  const activeView = useMemo(
    () =>
      activeViewId
        ? editorViews.find(
            (view) => view.id === parseInstanceId(activeViewId).viewId,
          )
        : undefined,
    [editorViews, activeViewId],
  );

  const editorSingletonTitle = useMemo(
    () =>
      activeView
        ? title(activeView.moduleId, activeView.title ?? activeView.id)
        : t('workbench.area.editor'),
    [activeView, title, t],
  );

  // The active module drives module-scoped default area visibility (§5.7): the
  // owner of the active editor view. Its `hiddenAreas` blacklist is applied as a
  // soft default in the layout (user can still toggle those areas open).
  const activeModuleId = activeView?.moduleId;
  const moduleHiddenAreas = useMemo<readonly LayoutArea[]>(
    () =>
      activeModuleId
        ? (contributions.hiddenAreas?.find((h) => h.moduleId === activeModuleId)
            ?.areas ?? NO_HIDDEN_AREAS)
        : NO_HIDDEN_AREAS,
    [activeModuleId, contributions.hiddenAreas],
  );

  // The `when` context every declarative condition is evaluated against
  // (架构方案 §5.9): contributed menus, keybindings, activityBar items and each
  // command's `enablement`. Two sources, merged here:
  //
  // - **module keys**, published through `context.setContextKey` and held by the
  //   host so every window agrees on them (见 03 §5.9);
  // - **platform keys**, derived from this window's live state — they stay local
  //   precisely because they differ per window (two windows have different active
  //   views), and they are spread last so a module cannot shadow one.
  //
  // Two axes a `when` might plausibly want are still absent on purpose rather
  // than stubbed: the host runtime (desktop/web), which the renderer has no
  // signal for, and detached-window-ness, which is constant here because a
  // detached window renders its own surface, not the workbench.
  const whenContext: WhenContext = useMemo(
    () => ({
      ...moduleContextKeys,
      'workbench.ready': contributionsLoaded,
      'workbench.online': online,
      'workbench.activeViewId': activeView?.id ?? '',
      'workbench.activeModuleId': activeModuleId ?? '',
      'workbench.activeArea': activeView?.area ?? '',
    }),
    [
      moduleContextKeys,
      contributionsLoaded,
      online,
      activeView,
      activeModuleId,
    ],
  );
  // `when` is existence, not pin: an item the clause drops should not appear
  // on the rail, in `/open`, or as a `workbench.open` target (见 03 / 21).
  // The join lives here — the clause is on the contribution, the projection
  // is the cross-window record — so the command/search providers stay generic.
  const visibleLaunchItems = useMemo(() => {
    if (launchItems === undefined || launchItems === null) {
      return [];
    }
    return filterLaunchItemsByWhen(
      launchItems,
      contributions.activityBar,
      whenContext,
    );
  }, [launchItems, contributions.activityBar, whenContext]);
  const railItems = useMemo(() => {
    // Loading or RPC failure: show manifest activityBar + builtins so the rail
    // is never reduced to settings-only while the host is catching up (见 21).
    if (launchItems === undefined || launchItems === null) {
      const filtered = contributions.activityBar.filter((item) =>
        evaluateWhen(item.when, whenContext),
      );
      return contributionFallbackRail(filtered);
    }
    return groupPinnedRail(visibleLaunchItems);
  }, [launchItems, visibleLaunchItems, contributions.activityBar, whenContext]);
  const whenContextRef = useCallback(() => whenContext, [whenContext]);

  // Chord hints for the search results, taken from the resolved table (§5.9) so
  // a rebound chord shows the chord the user actually has — and spelled for
  // this platform and keyboard layout, which is the whole reason the hint is
  // formatted here rather than by the provider.
  const { bindings: resolvedKeybindings, layout, mac } = useKeybindings();
  const chordHints = useMemo(() => {
    const hints = new Map<string, string>();
    for (const entry of resolvedKeybindings) {
      // Ascending precedence, so the last writer for a command wins — the same
      // binding the dispatcher would pick.
      if (entry.chord && entry.problem === undefined) {
        hints.set(
          entry.binding.command,
          formatChord(entry.chord, { mac, ...(layout ? { layout } : {}) }),
        );
      }
    }
    return hints;
  }, [resolvedKeybindings, layout, mac]);

  // Global search (需求 FR-14 §4). Everything the providers read is already
  // workbench state, so opening the field costs no round-trip (§4.1 <100ms) —
  // only the recent list is fetched, and that happens on mount below.
  const userActivation = useUserActivation();
  const searchActivator = useMemo(
    () =>
      createLaunchActivator({
        views: contributions.views,
        detachedSurfaces,
        runningExternalProcesses,
        moduleService,
        launcherService,
        openExternalBridge,
        userActivation,
        onDispatch: dispatch,
        onOpenView: openView,
        onFocusDetached,
        onActivateExternal,
        onReportNavigation: reportNavigation,
        translator: { t, title },
      }),
    [
      contributions.views,
      detachedSurfaces,
      runningExternalProcesses,
      moduleService,
      launcherService,
      openExternalBridge,
      userActivation,
      dispatch,
      openView,
      onFocusDetached,
      onActivateExternal,
      reportNavigation,
      t,
      title,
    ],
  );

  const [recentSearches, setRecentSearches] = useState<
    readonly SearchRecentEntry[]
  >([]);
  const reloadRecentSearches = useCallback(() => {
    searchRecentService
      .list()
      .then(setRecentSearches)
      .catch((err: unknown) => {
        logBridge?.append({
          level: 'warn',
          kind: 'log',
          scope: 'search',
          message: 'Failed to read recent searches; the section stays empty',
          error: {
            message: err instanceof Error ? err.message : String(err),
          },
        });
      });
  }, [searchRecentService, logBridge]);
  useEffect(() => {
    reloadRecentSearches();
  }, [reloadRecentSearches]);
  // The list follows the partition key. A key change is the one time every
  // window reloads it; ordinary picks stay local until the box opens again.
  useEffect(() => {
    return client.listen(PARTITION_CHANGED_EVENT, () => {
      reloadRecentSearches();
    });
  }, [client, reloadRecentSearches]);

  // Module-declared search sections, `when`-gated like any other face. Pure
  // manifest data, so the app tab strip is known without activating anything.
  const searchSections = useMemo(
    () =>
      contributions.search.filter((section) =>
        evaluateWhen(section.when, whenContext),
      ),
    [contributions.search, whenContext],
  );

  // Module-declared search filters, `when`-gated the same way. Also pure
  // manifest data — a `picker`'s candidates are what needs a live module, and
  // only once the user opens it.
  const searchFilters = useMemo(
    () =>
      contributions.searchFilters.filter((filter) =>
        evaluateWhen(filter.when, whenContext),
      ),
    [contributions.searchFilters, whenContext],
  );

  // 最新的目标表交给 `workbench.open` 的 handler（它读 ref 以保持稳定身份，见上）。
  openTargetsRef.current = {
    items: visibleLaunchItems,
    activate: searchActivator,
  };

  const searchProviders = useMemo(() => {
    const launchDeps = () => ({
      items: visibleLaunchItems,
      translator: { t, title },
    });
    const commandDeps = () => ({
      commands: allCommands,
      context: whenContext,
      resolveTitle: title,
      mru: commandMru.list(),
      chordHints,
    });
    const sources = [
      createCommandProvider(commandDeps),
      createOpenCommandProvider(launchDeps),
    ];
    // Resolving a stored pick means asking its provider what that key looks
    // like *today* — see `SearchRecentEntry` on why nothing is snapshotted.
    const lookup = createRecentLookup(sources);
    return [
      createRecentProvider(() => ({
        entries: recentSearches,
        lookup,
        translator: { t, locale },
      })),
      ...sources,
      // App-tab sections a module declared. `providerAnswers` keeps these out
      // of the All tab, so they only run once the user picks that app's tab.
      // Straight onto `moduleService.executeCommand`, not the workbench
      // `dispatch`: this call needs the command's return value, which the
      // dispatcher discards (it only looks for an `OpenViewRequest`).
      ...createModuleSearchProviders(searchSections, {
        executeCommand: (id, ...args) =>
          moduleService.executeCommand(id, ...args),
      }),
      // …plus that app's own commands, so its tab stays a scope filter rather
      // than a different feature set (见 `createAppCommandProvider`).
      ...appTabModuleIds(searchSections).map((moduleId) =>
        createAppCommandProvider(moduleId, commandDeps),
      ),
    ];
  }, [
    visibleLaunchItems,
    allCommands,
    whenContext,
    chordHints,
    commandMru,
    recentSearches,
    searchSections,
    moduleService,
    t,
    title,
    locale,
  ]);

  // The header's Create menu (见 03 header 槽位) — the first face to consume
  // `contributes.menus`. Pure manifest data plus the two `when` axes, so it
  // costs no activation: a module lands in this menu without ever being opened,
  // and only activates when a row is actually clicked (`onCommand:<id>`).
  const createMenu = useMemo(
    () =>
      resolveMenuLocation({
        location: MENU_LOCATION.headerCreate,
        menus: contributions.menus,
        commands: allCommands,
        submenus: contributions.submenus,
        context: whenContext,
        resolveTitle: title,
      }),
    [
      contributions.menus,
      contributions.submenus,
      allCommands,
      whenContext,
      title,
    ],
  );
  useEffect(() => {
    if (
      createMenu.danglingCommands.length === 0 &&
      createMenu.danglingSubmenus.length === 0
    ) {
      return;
    }
    // A placement naming a command or submenu nobody declares would otherwise
    // be a row that silently never appears — indistinguishable from a `when`
    // that did not hold. Only this case is reported; the conditional drops
    // are the mechanism working.
    if (createMenu.danglingCommands.length > 0) {
      logBridge?.append({
        level: 'warn',
        kind: 'log',
        scope: 'menus',
        message: `${MENU_LOCATION.headerCreate}: placement names an undeclared command`,
        data: { commands: createMenu.danglingCommands },
      });
    }
    if (createMenu.danglingSubmenus.length > 0) {
      logBridge?.append({
        level: 'warn',
        kind: 'log',
        scope: 'menus',
        message: `${MENU_LOCATION.headerCreate}: placement names an undeclared or cyclic submenu`,
        data: { submenus: createMenu.danglingSubmenus },
      });
    }
  }, [createMenu.danglingCommands, createMenu.danglingSubmenus, logBridge]);

  const runSearchResult = useCallback(
    (result: SearchResult, anchor?: SearchHitAnchor) => {
      switch (result.action.type) {
        case 'run':
          result.action.run();
          return;
        case 'command':
          dispatch(
            result.action.command,
            ...commandArgsWithAnchor(result.action, anchor),
          );
          return;
        case 'openView':
          routeOpenView(result.action.viewId, result.action.instanceKey);
          return;
      }
    },
    [dispatch, routeOpenView],
  );

  /**
   * Record a pick, but **do not refresh the recent list while the dropdown is
   * still open** — reload once it closes instead.
   *
   * Most picks close the box, so this used to be invisible. An `inPlace` pick
   * keeps it open, and reloading would grow the `最近搜索` section under the
   * user: every row below it shifts, and the card that just opened beside the
   * picked row is suddenly beside a different one. Same reasoning as 需求 §9's
   * "各窗口共享同一份，但不实时通知" — the recent list must not move while
   * someone is reading the results above it.
   */
  const recordSearchPick = useCallback(
    (providerId: string, key: string, snapshot?: SearchRecentSnapshot) => {
      searchRecentService
        .record(providerId, key, snapshot)
        .then(() => {
          if (!searchOpenRef.current) {
            reloadRecentSearches();
          }
        })
        .catch((err: unknown) => {
          logBridge?.append({
            level: 'warn',
            kind: 'log',
            scope: 'search',
            message: 'Failed to record a recent search',
            error: {
              message: err instanceof Error ? err.message : String(err),
            },
          });
        });
    },
    [searchRecentService, reloadRecentSearches, logBridge],
  );

  const runSearchSuggestion = useCallback(
    (suggestion: SearchSuggestion) => {
      if ('id' in suggestion) {
        dispatch(suggestion.id);
        return;
      }
      searchActivator(suggestion, 'search');
    },
    [dispatch, searchActivator],
  );

  // The chord listener itself lives one level up, in `KeybindingHost`, so a
  // detached window gets one too (§5.9). What only the workbench can supply is
  // handed over here: its `when` context, its command table — the same one the
  // search box filters, so a chord cannot fire a command the list would refuse
  // to show (§5.9 `enablement`) — and its dispatcher, which is the only path
  // that reaches the built-in command handlers.
  useKeybindingDispatch({
    getContext: whenContextRef,
    getCommands: () => allCommands,
    executeCommand: (command, ...args) => dispatch(command, ...args),
  });

  // present(面) on navigate (架构方案 §5.8 跳转, 见 8): when a `navigate(view:
  // <id>, …)` arrives, open/select the target view so it mounts — the location
  // (点) is then delivered to it via NavigationContext/useViewNavigation (which
  // reads the buffered latest on mount). Makes navigate "open + locate" atomic.
  // Only editor-area views need opening (rail/layout views are always mounted);
  // a detached view is presented by its own window, so skip the inline open.
  useEffect(() => {
    if (!navigation) {
      return;
    }
    return navigation.subscribeAll((viewId, location, instanceKey) => {
      if (isDetachedInstance(detachedSurfaces, viewId, instanceKey)) {
        return;
      }
      const view = contributions.views.find((v) => v.id === viewId);
      if (view?.area === 'editor') {
        // Through the same router a command's `OpenViewRequest` takes, so an
        // instance-keyed navigation opens/focuses its own tab while a keyless one
        // still just activates the view. Open-**or**-focus falls out of the
        // instance id being the identity: navigating the same document twice
        // raises the tab already open instead of stacking a second one (见 20).
        routeOpenView(viewId, instanceKey);
      }
    });
  }, [navigation, detachedSurfaces, contributions.views, routeOpenView]);

  // An iframe page named its own tab (见 3 / 11). Both tab hosts share one
  // DockviewApi, so the editor host finds the panel even after it is dragged
  // onto the auxiliary strip. A window that does not hold it no-ops.
  useEffect(() => {
    if (!viewTitles) {
      return;
    }
    return viewTitles.subscribe((viewId, instanceKey, title) => {
      editorTabHost.setTitle(viewId, instanceKey, title);
    });
  }, [viewTitles, editorTabHost]);

  // Commands asked for by native chrome the main process draws — the tray menu
  // (见 23). Routed through `dispatch`, the same entry a keybinding and a palette
  // entry use, so the tray gets built-ins and module commands alike without the
  // main process learning what any of them mean.
  useEffect(() => {
    return commandRequests?.subscribe((command, args) => {
      void dispatch(command, ...args);
    });
  }, [commandRequests, dispatch]);

  // Broadcast editor navigation (platform.workbench.navigationChanged).
  useEffect(() => {
    if (!activeViewId || !activeView) {
      return;
    }
    reportNavigation({
      source: 'editor',
      moduleId: activeView.moduleId,
      viewId: activeView.id,
    });
  }, [activeViewId, activeView, reportNavigation]);

  return (
    <UiClientConfigProvider portalContainer={resolveOverlayPortalContainer()}>
      <NativeContentsSuppressionProvider suppressed={nativeContentsSuppressed}>
        <OverlayProvider
          client={client}
          overlayService={overlayService}
          views={contributions.views}
          registry={registry}
          moduleService={moduleService}
          onOpenChange={setOverlayOpen}
          presentWindow={
            windowControlsBridge
              ? () => windowControlsBridge.present()
              : undefined
          }
        >
          <CaptureOverlayApi target={overlayApiRef} />
          <WorkbenchLayout
            schema={DEFAULT_LAYOUT}
            views={layoutViews}
            allViews={contributions.views}
            registry={registry}
            moduleService={moduleService}
            activeModuleId={activeModuleId}
            moduleHiddenAreas={moduleHiddenAreas}
            onOpenView={routeOpenView}
            iframeBridge={iframeBridge}
            externalBridge={externalBridge}
            detachBridge={detachBridge}
            tabHost={auxiliaryTabHost}
            editorTabHost={editorTabHost}
            editorSingletonTitle={editorSingletonTitle}
            contributionsReady={contributionsLoaded}
            onDetachView={onDetachView}
            detachedSurfaces={detachedSurfaces}
            onAttachView={onAttachView}
            onFocusDetached={onFocusDetached}
            subscribeContributionsChanged={subscribeContributionsChanged}
            sidebarOverlay={
              <NotificationPrompt
                client={client}
                executeCommand={(command, ...args) =>
                  moduleService.executeCommand(command, ...args)
                }
              />
            }
            slots={{
              header: (
                <HeaderSlot
                  bridge={windowControlsBridge}
                  error={error}
                  commandCount={contributions.commands.length}
                  viewCount={contributions.views.length}
                  badge={badge}
                  online={online}
                  themeService={themeService}
                  onRefresh={refresh}
                  onOpenWindow={onOpenWindow}
                  search={{
                    providers: searchProviders,
                    open: searchOpen,
                    ...(searchSeed ? { seed: searchSeed } : {}),
                    onOpenChange: (next) => {
                      searchOpenRef.current = next;
                      setSearchOpen(next);
                      // Picks made while it was open deferred their refresh
                      // (见 `recordSearchPick`); settle up now.
                      if (!next) {
                        reloadRecentSearches();
                      }
                    },
                    onRecord: recordSearchPick,
                    onRun: runSearchResult,
                    searchSections,
                    searchFilters,
                    // Straight onto the module service, not the workbench
                    // dispatcher: a picker needs the command's return value,
                    // which `dispatch` discards (同 `createModuleSearchProviders`).
                    executeCommand: (id, ...args) =>
                      moduleService.executeCommand(id, ...args),
                    // Lets the dropdown hold itself open under an in-place
                    // hit's card (见 `header-search.tsx` `AMBIENT_CLOSE`).
                    platformOverlayOpen: overlayOpen,
                    launchItems: launchItems ?? [],
                    suggestionSources: {
                      commands: allCommands,
                      context: whenContext,
                      resolveTitle: title,
                      launchItems: visibleLaunchItems,
                    },
                    onRunSuggestion: runSearchSuggestion,
                  }}
                  create={{
                    entries: createMenu.entries,
                    onRun: (command) => dispatch(command),
                  }}
                />
              ),
              activityBar: (
                <ActivitySlot
                  railItems={railItems}
                  views={contributions.views}
                  registry={registry}
                  moduleService={moduleService}
                  launcherService={launcherService}
                  openExternalBridge={openExternalBridge}
                  iframeBridge={iframeBridge}
                  externalBridge={externalBridge}
                  subscribeContributionsChanged={subscribeContributionsChanged}
                  detachedSurfaces={detachedSurfaces}
                  runningExternalProcesses={runningExternalProcesses}
                  distFetchingModules={distFetchingModules}
                  activeViewId={activeViewId}
                  onDispatch={dispatch}
                  onOpenView={openView}
                  onFocusDetached={onFocusDetached}
                  onActivateExternal={onActivateExternal}
                  onDetachView={onDetachView}
                  onAttachView={onAttachView}
                  onReportNavigation={reportNavigation}
                />
              ),
              editor: (
                <EditorSlot
                  key={editorScopeEpoch}
                  activeViewId={activeViewId}
                  activeView={activeView}
                  editorViews={editorViews}
                  detachedSurfaces={detachedSurfaces}
                  onOpenView={openView}
                  onFocusDetached={onFocusDetached}
                  onAttachView={onAttachView}
                  onDetachView={onDetachView}
                  registry={registry}
                  moduleService={moduleService}
                  iframeBridge={iframeBridge}
                  externalBridge={externalBridge}
                  detachBridge={detachBridge}
                  subscribeContributionsChanged={subscribeContributionsChanged}
                />
              ),
              auxiliaryBar: <></>,
            }}
          />
          <PermissionPrompt
            permissionService={permissionService}
            client={client}
            onOpenChange={setPermissionPromptOpen}
          />
          {/* In-app toast surface (架构方案 §5.15): one Toaster renders every
            toast() raised by NotificationToast / CrashToast (headless listeners). */}
          <Toaster />
          {/* Update prompt (见 25): mounted persistently and self-hiding, so the
            decision of whether an update is worth showing stays in the host
            rather than being duplicated as workbench state. */}
          <UpdatePrompt
            updateService={updateService}
            client={client}
            launcherService={launcherService}
            openExternalBridge={openExternalBridge}
          />
          <NotificationToast
            client={client}
            onBadgeChange={setBadge}
            {...(inAppNotifications ? { inAppNotifications } : {})}
          />
          {logBridge ? <CrashToast bridge={logBridge} /> : null}
          {logBridge && mirrorConsole ? (
            <LogConsoleMirror bridge={logBridge} />
          ) : null}
        </OverlayProvider>
      </NativeContentsSuppressionProvider>
    </UiClientConfigProvider>
  );
}
